import { useEffect, useState } from 'react';
import axios from 'axios';
import { ShoppingCart, Truck, Plus, Trash2, Edit2, Save, X, Tag, Headphones, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSettingsManagement = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState({
    show_promo_code: true,
    show_delivery_options: true,
    livechat_3cx_enabled: false,
    whatsapp_enabled: true,
    whatsapp_number: '+2290129725590',
  });
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newOption, setNewOption] = useState({
    name: '',
    description: '',
    price: 0,
    estimated_days_min: 5,
    estimated_days_max: 8,
    is_active: true,
    sort_order: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, optionsRes] = await Promise.all([
        axios.get(`${API}/admin/checkout-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/delivery-options`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSettings(settingsRes.data);
      setDeliveryOptions(optionsRes.data);
    } catch (error) {
      toast.error('Failed to fetch checkout settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/checkout-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Checkout settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDeliveryOption = async () => {
    if (!newOption.name.trim()) {
      toast.error('Please enter a name for the delivery option');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/delivery-options`, newOption, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeliveryOptions([...deliveryOptions, response.data]);
      setNewOption({
        name: '',
        description: '',
        price: 0,
        estimated_days_min: 5,
        estimated_days_max: 8,
        is_active: true,
        sort_order: 0
      });
      setShowAddForm(false);
      toast.success('Delivery option added!');
    } catch (error) {
      toast.error('Failed to add delivery option');
    }
  };

  const handleUpdateDeliveryOption = async (option) => {
    try {
      await axios.put(`${API}/admin/delivery-options/${option.id}`, option, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeliveryOptions(deliveryOptions.map(o => o.id === option.id ? option : o));
      setEditingOption(null);
      toast.success('Delivery option updated!');
    } catch (error) {
      toast.error('Failed to update delivery option');
    }
  };

  const handleDeleteDeliveryOption = async (optionId) => {
    if (!window.confirm('Are you sure you want to delete this delivery option?')) return;
    
    try {
      await axios.delete(`${API}/admin/delivery-options/${optionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeliveryOptions(deliveryOptions.filter(o => o.id !== optionId));
      toast.success('Delivery option deleted!');
    } catch (error) {
      toast.error('Failed to delete delivery option');
    }
  };

  const handleToggleOptionActive = async (option) => {
    const updated = { ...option, is_active: !option.is_active };
    await handleUpdateDeliveryOption(updated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="checkout-settings-management">
      {/* Checkout Display Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Checkout Page Settings
          </CardTitle>
          <CardDescription>
            Control what options are visible to customers during checkout
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Tag className="h-5 w-5 text-gray-500" />
              <div>
                <p className="font-medium">Promo Code Field</p>
                <p className="text-sm text-gray-600">
                  Allow customers to enter discount codes at checkout
                </p>
              </div>
            </div>
            <Switch
              data-testid="promo-code-toggle"
              checked={settings.show_promo_code}
              onCheckedChange={(checked) => setSettings({ ...settings, show_promo_code: checked })}
            />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Truck className="h-5 w-5 text-gray-500" />
              <div>
                <p className="font-medium">Delivery Options</p>
                <p className="text-sm text-gray-600">
                  Show delivery method selection during checkout
                </p>
              </div>
            </div>
            <Switch
              data-testid="delivery-options-toggle"
              checked={settings.show_delivery_options}
              onCheckedChange={(checked) => setSettings({ ...settings, show_delivery_options: checked })}
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-checkout-settings-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Customer Chat Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Customer Chat Channels
          </CardTitle>
          <CardDescription>
            Control which chat channels appear on the storefront. WhatsApp is gated
            to customers who have already placed a paid order.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Headphones className="h-5 w-5 text-gray-500" />
              <div>
                <p className="font-medium">3CX Live Chat & Call</p>
                <p className="text-sm text-gray-600">
                  Show the 3CX bubble and the &quot;Talk to Sales&quot; navbar button
                </p>
              </div>
            </div>
            <Switch
              data-testid="livechat-3cx-toggle"
              checked={settings.livechat_3cx_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, livechat_3cx_enabled: checked })}
            />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <MessageCircle className="h-5 w-5 text-emerald-600" />
              <div>
                <p className="font-medium">WhatsApp (paid customers only)</p>
                <p className="text-sm text-gray-600">
                  Show the floating WhatsApp bubble. Only signed-in customers with a
                  paid order can reach sales; everyone else sees a friendly gated modal.
                </p>
              </div>
            </div>
            <Switch
              data-testid="whatsapp-toggle"
              checked={settings.whatsapp_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, whatsapp_enabled: checked })}
            />
          </div>

          <div className="p-4 border rounded-lg space-y-2">
            <Label htmlFor="whatsapp-number">WhatsApp Number (international format)</Label>
            <Input
              id="whatsapp-number"
              data-testid="whatsapp-number-input"
              value={settings.whatsapp_number}
              onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
              placeholder="+2290129725590"
            />
            <p className="text-xs text-gray-500">
              Format: country code + number, no spaces. Example: +2290129725590
            </p>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-chat-settings-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Chat Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delivery Options Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Delivery Options
              </CardTitle>
              <CardDescription>
                Manage available delivery methods and their prices
              </CardDescription>
            </div>
            <Button onClick={() => setShowAddForm(true)} data-testid="add-delivery-option-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Option
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Add New Option Form */}
          {showAddForm && (
            <div className="mb-6 p-4 border rounded-lg bg-gray-50">
              <h4 className="font-medium mb-4">Add New Delivery Option</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    data-testid="new-delivery-name"
                    value={newOption.name}
                    onChange={(e) => setNewOption({ ...newOption, name: e.target.value })}
                    placeholder="Standard Shipping"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Price ($)</Label>
                  <Input
                    data-testid="new-delivery-price"
                    type="number"
                    step="0.01"
                    value={newOption.price}
                    onChange={(e) => setNewOption({ ...newOption, price: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Description</Label>
                  <Input
                    data-testid="new-delivery-description"
                    value={newOption.description}
                    onChange={(e) => setNewOption({ ...newOption, description: e.target.value })}
                    placeholder="Estimated 5-8 business days"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Min Days</Label>
                  <Input
                    type="number"
                    value={newOption.estimated_days_min}
                    onChange={(e) => setNewOption({ ...newOption, estimated_days_min: parseInt(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Max Days</Label>
                  <Input
                    type="number"
                    value={newOption.estimated_days_max}
                    onChange={(e) => setNewOption({ ...newOption, estimated_days_max: parseInt(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={newOption.sort_order}
                    onChange={(e) => setNewOption({ ...newOption, sort_order: parseInt(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-center gap-2 mt-6">
                  <Switch
                    checked={newOption.is_active}
                    onCheckedChange={(checked) => setNewOption({ ...newOption, is_active: checked })}
                  />
                  <Label>Active</Label>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleAddDeliveryOption} data-testid="confirm-add-delivery-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Option
                </Button>
                <Button variant="outline" onClick={() => setShowAddForm(false)}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Delivery Options List */}
          {deliveryOptions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Truck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No delivery options configured</p>
              <p className="text-sm mt-1">Add delivery options for customers to choose from</p>
            </div>
          ) : (
            <div className="space-y-3">
              {deliveryOptions.map((option) => (
                <div
                  key={option.id}
                  className={`p-4 border rounded-lg ${!option.is_active ? 'bg-gray-50 opacity-60' : ''}`}
                  data-testid={`delivery-option-${option.id}`}
                >
                  {editingOption?.id === option.id ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Name</Label>
                        <Input
                          value={editingOption.name}
                          onChange={(e) => setEditingOption({ ...editingOption, name: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Price ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editingOption.price}
                          onChange={(e) => setEditingOption({ ...editingOption, price: parseFloat(e.target.value) || 0 })}
                          className="mt-1"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Description</Label>
                        <Input
                          value={editingOption.description || ''}
                          onChange={(e) => setEditingOption({ ...editingOption, description: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Min Days</Label>
                        <Input
                          type="number"
                          value={editingOption.estimated_days_min}
                          onChange={(e) => setEditingOption({ ...editingOption, estimated_days_min: parseInt(e.target.value) || 0 })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Max Days</Label>
                        <Input
                          type="number"
                          value={editingOption.estimated_days_max}
                          onChange={(e) => setEditingOption({ ...editingOption, estimated_days_max: parseInt(e.target.value) || 0 })}
                          className="mt-1"
                        />
                      </div>
                      <div className="flex gap-2 mt-4 md:col-span-2">
                        <Button onClick={() => handleUpdateDeliveryOption(editingOption)}>
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </Button>
                        <Button variant="outline" onClick={() => setEditingOption(null)}>
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{option.name}</h4>
                          {!option.is_active && (
                            <span className="text-xs bg-gray-200 px-2 py-1 rounded">Inactive</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {option.description || `${option.estimated_days_min}-${option.estimated_days_max} business days`}
                        </p>
                        <p className="text-lg font-semibold text-green-600 mt-1">
                          {option.price === 0 ? 'Free' : `$${option.price.toFixed(2)}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={option.is_active}
                          onCheckedChange={() => handleToggleOptionActive(option)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingOption(option)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteDeliveryOption(option.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckoutSettingsManagement;
